import { H4 } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useCallback } from 'react';

import { useAppSelector } from '@/redux/hooks';
import { getCatalogDetailCurators } from '@/redux/modules/catalog/detail/catalogDetail.selectors';
import { getCuratorBaseUrl } from '@/redux/modules/config';
import { Curator as CuratorType } from '@/types/Curator';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { getCuratorImageUrl } from '@/utils/urls';

import { Curator } from './Curator';
import { Curators } from './Curators';

type Props = {
    catalogId: number;
    className?: string;
};

export const CatalogCurators = ({ catalogId, className }: Props) => {
    const formatMessage = useAppFormatMessage();
    const curators = useAppSelector((state) => getCatalogDetailCurators(state, catalogId)) || [];
    const curatorImgUrl = useAppSelector(getCuratorBaseUrl);
    const multipleCurators = curators.length > 1;

    const CuratorComponent = multipleCurators ? Curators : Curator;
    const size = multipleCurators ? 160 : 280;

    const mapCurators = useCallback(
        ({ expertise, id, imageFileName, name }: CuratorType) => {
            return (
                <CuratorComponent
                    expertise={expertise}
                    id={id}
                    imgSrc={getCuratorImageUrl(curatorImgUrl, id, imageFileName, size)}
                    key={`${id}-${name}`}
                    name={name}
                />
            );
        },
        [CuratorComponent, curatorImgUrl, size]
    );

    return curators.length ? (
        <div className={cn('min-w-[250px] border-l border-border-secondary xsMax:border-l-0', className)}>
            <div
                className={cn('my-lg flex flex-col pl-sm', {
                    'items-center': !multipleCurators,
                })}
            >
                <H4
                    className="mb-sm uppercase"
                    color="secondary"
                >
                    {formatMessage([multipleCurators ? 'auction_curators' : 'auction_curator'])}:
                </H4>
                <div
                    className={multipleCurators ? 'flex w-full flex-col items-stretch justify-evenly' : ''}
                    id="curators-list"
                >
                    {curators.map(mapCurators)}
                </div>
            </div>
        </div>
    ) : null;
};
